/* eslint-disable react/require-default-props */
/* eslint-disable react/no-danger */
import React, { useEffect, useRef } from 'react';
import Slider from 'react-slick';
import propTypes from 'prop-types';

function SlideTitle({ content, type = 'marketing' }) {
  return content ? (
    <h3 className={`cmp-${type}-carousel__title`}>{content}</h3>
  ) : null;
}

function SlideDates({ content, type = 'marketing' }) {
  return content ? (
    <p className={`cmp-${type}-carousel__dates`}>{content}</p>
  ) : null;
}

function SlideBody({ content, type = 'marketing', allowHtml = false }) {
  if (!content) {
    return null;
  }

  return allowHtml ? (
    <p
      className={`cmp-${type}-carousel__body-text`}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  ) : (
    <p className={`cmp-${type}-carousel__body-text`}>{content}</p>
  );
}
function SlideFooter({ content, type = 'marketing' }) {
  return content ? (
    <p className={`cmp-${type}-carousel__footer`}>{content}</p>
  ) : null;
}

function SlideLink({ link = {}, handleFocus }) {
  return link.title && link.href ? (
    <a className="cmp-cta-link" href={link.href} onFocus={handleFocus}>
      <span>{link.title}</span>
    </a>
  ) : null;
}

// fix off-by-one errors caused by timezones
const dateInLocalTimezone = (dateString) => {
  const date = new Date(dateString);
  const timeZoneOffset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() + timeZoneOffset);

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

// format the start/end dates
const formatDates = (startDate, endDate) => {
  if (startDate && endDate) {
    return `${dateInLocalTimezone(startDate)} - ${dateInLocalTimezone(endDate)}`;
  }

  if (startDate) {
    return `Starting ${dateInLocalTimezone(startDate)}`;
  }

  if (endDate) {
    return `Expires ${dateInLocalTimezone(endDate)}`;
  }

  return null;
};

function Carousel({ slides, type = 'marketing', autoplay }) {
  const sliderRef = useRef();

  const goToSlide = (index) => () => {
    sliderRef.current?.slickGoTo(index);

    // focusing on an element will scroll the container in a non-helpful way
    // reset the scroll position back to zero so the CSS transform works
    const slickList = sliderRef.current?.innerSlider.list;
    if (slickList) {
      slickList.scrollLeft = 0;
    }
  };

  useEffect(() => {
    if (!autoplay) {
      return;
    }

    const slickList = sliderRef.current?.innerSlider.list;

    if (slickList) {
      slickList.addEventListener('focusin', () => {
        sliderRef.current?.slickPause();
      });
    }

    const handleOutsideInteraction = (e) => {
      if (!slickList.contains(e.target)) {
        sliderRef.current?.slickPlay();
      }
    };

    document.body.addEventListener('focusin', handleOutsideInteraction);
    document.body.addEventListener('click', handleOutsideInteraction);

    // eslint-disable-next-line consistent-return
    return () => {
      document.body.removeEventListener('focusin', handleOutsideInteraction);
      document.body.removeEventListener('click', handleOutsideInteraction);
    };
  }, [autoplay]);

  return slides ? (
    <Slider
      dots
      accessibility
      swipeToSlide
      infinite={autoplay}
      arrows={false}
      speed={300}
      autoplaySpeed={4000}
      slidesToShow={1}
      slidesToScroll={1}
      variableWidth={false}
      ref={sliderRef}
      autoplay={autoplay}
      pauseOnHover
    >
      {slides.map((slide, index) => {
        if (type === 'marketing') {
          return (
            <div key={slide.id}>
              <div
                className="cmp-marketing-carousel__image"
                style={{
                  backgroundImage: `url(${slide?.image?.url})`,
                }}
              >
                <SlideTitle content={slide.title} />
                <SlideBody content={slide.body} allowHtml />
                <SlideFooter content={slide.footer} />
                <SlideLink link={slide.link} handleFocus={goToSlide(index)} />
              </div>
            </div>
          );
        }

        if (type === 'deals') {
          return (
            <div key={slide.id} className="cmp-deals-carousel__slide">
              <div className="cmp-deals-carousel__content">
                <SlideTitle content={slide.summary} type="deals" />
                <SlideBody content={slide.description} allowHtml type="deals" />
                <SlideDates content={formatDates(slide.startDate, slide.endDate)} type="deals" />
                <div className="cmp-deals-carousel__link">
                  <SlideLink
                    link={{
                      title: slide.callToAction,
                      href: `/deals/${slide.slug}`,
                    }}
                    handleFocus={goToSlide(index)}
                  />
                </div>
              </div>
              <div
                className="cmp-deals-carousel__image"
                style={{
                  backgroundImage: `url(${slide.imageUrl || '/themes/custom/tire_discounters/public/images/default-deals-image.png'})`,
                }}
              />
            </div>
          );
        }

        return null;
      })}
    </Slider>
  ) : null;
}

SlideTitle.propTypes = {
  content: propTypes.string,
  type: propTypes.string,
};

SlideDates.propTypes = {
  content: propTypes.string,
  type: propTypes.string,
};

SlideBody.propTypes = {
  content: propTypes.string,
  type: propTypes.string,
  allowHtml: propTypes.bool,
};

SlideFooter.propTypes = {
  content: propTypes.string,
  type: propTypes.string,
};

SlideLink.propTypes = {
  link: propTypes.shape({
    title: propTypes.string,
    href: propTypes.string,
  }),
  handleFocus: propTypes.func,
};

Carousel.propTypes = {
  slides: propTypes.arrayOf(
    propTypes.oneOfType([
      propTypes.shape({
        id: propTypes.string,
        image: propTypes.shape({
          mediaId: propTypes.string,
          alt: propTypes.string,
          url: propTypes.string,
          width: propTypes.number,
          height: propTypes.number,
        }),
        title: SlideTitle.propTypes.content,
        body: SlideBody.propTypes.content,
        footer: SlideFooter.propTypes.content,
        link: SlideLink.propTypes.link,
      }),
      propTypes.shape({
        id: propTypes.number,
        imageUrl: propTypes.string,
        title: propTypes.string,
        slug: propTypes.string,
      }),
    ]),
  ),
  type: propTypes.string,
  autoplay: propTypes.bool,
};

export default Carousel;
